import fileImageIcon from '@images/icons/file-pdf.png';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import {
  bjToast, ImageBj,
} from '@components/common';
import { ChevronDownIcon } from '@heroicons/react/24/solid';
import { useTranslation } from 'next-i18next';
import { useCallback, useState } from 'react';
import { createDownloadLink, fetcher, ResponseWithHeaders } from '@utils/data-fetching';
import { CvDetailObject } from '../interface/CvDetailInterface';

export const ProfileAttachments = ({ cvDetail, locale }: { cvDetail: CvDetailObject; locale: Locale }) => {
  const { t } = useTranslation('common');

  const DEFAULT_ATTACHMENTS = 3;
  const [numberOfAttachments, setNumberOfAttachments] = useState<number>(DEFAULT_ATTACHMENTS);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const handleDownload = useCallback(async (downloadLink: string, name: string, id: string) => {
    try {
      setDownloadingId(id);
      const downloadPath = downloadLink.startsWith('http') ? new URL(downloadLink).pathname : downloadLink;
      const res = await fetcher<unknown>(downloadPath, locale, {
        method: 'GET',
        responseType: 'blob',
        withResponseHeaders: true,
      });
      const { response, responseHeaders } = res as ResponseWithHeaders<BlobPart>;
      const contentDisposition = responseHeaders.get('content-disposition');
      const filenameMatch = contentDisposition?.match(/filename[^;=\n]*=(['"].*?['"]|[^;\n]*)/);
      const filename = filenameMatch?.[1]?.replace(/['"]/g, '') || name;
      createDownloadLink(response, filename);
    } catch (error) {
      console.error('Download failed:', error);
      bjToast.error(t('error.message.download_failed'));
    } finally {
      setDownloadingId(null);
    }
  }, [locale, t]);

  if (!cvDetail?.privateInformation?.attachments || cvDetail.privateInformation?.attachments?.length === 0) {
    return null;
  }

  return (
    <section>
      <div className="my-3 flex flex-col gap-2">
        {cvDetail.privateInformation.attachments.slice(0, numberOfAttachments).map((item) => (
          <div key={item.id} className="flex items-center justify-between rounded-full bg-surface-100 px-4 py-2">
            <div className="flex items-center gap-2">
              <ImageBj src={fileImageIcon} alt="File" className="mr-3" width={20} height={26} />
              <div className="block text-xs font-normal">
                {item?.name?.length > 25 ? `${item?.name?.substring(0, 25)}...` : item?.name || ''}
              </div>
            </div>
            <div>
              <button
                type="button"
                onClick={() => { void handleDownload(item.downloadLink, item.name, item.id); }}
                disabled={downloadingId === item.id}
              >
                <ArrowDownTrayIcon className="size-5 text-ink" />
              </button>
            </div>
          </div>
        ))}

        {/* See More Button */}
        {numberOfAttachments === DEFAULT_ATTACHMENTS
          && cvDetail.privateInformation?.attachments?.length > DEFAULT_ATTACHMENTS && (
            <div
              className="my-2 flex cursor-pointer items-center"
              role="presentation"
              onClick={() => setNumberOfAttachments(cvDetail?.privateInformation?.attachments?.length ?? DEFAULT_ATTACHMENTS)}
            >
              <span className="mr-2 block min-w-fit text-sm text-primary">
                {t('global.btn.see_more')}
              </span>
              <ChevronDownIcon className="inline-block size-5 text-primary" />
            </div>
        )}

        {/* See Less Button */}
        {numberOfAttachments > DEFAULT_ATTACHMENTS && (
          <div
            className="my-2 flex cursor-pointer items-center"
            role="presentation"
            onClick={() => setNumberOfAttachments(DEFAULT_ATTACHMENTS)}
          >
            <span className="mr-2 block min-w-fit text-sm text-primary">
              {t('global.btn.see.less')}
            </span>
            <ChevronDownIcon className="inline-block size-5 rotate-180 text-primary" />
          </div>
        )}
      </div>
    </section>
  );
};
